import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useDebounce } from '@/hooks/useDebounce';
import { useOrganisationsInfinite, useOrganisation } from '../hooks';

interface OrganisationsFilterComboboxProps {
  /** Selected mongo org id. Pass 'all' or undefined for no selection. */
  value?: string;
  /** Called with the selected mongo org id, or 'all' when the user clears the filter. */
  onChange: (orgId: string) => void;
  disabled?: boolean;
  className?: string;
  /** Placeholder and "reset" label. Defaults to 'All Accounts'. */
  placeholder?: string;
}

const ALL_VALUE = 'all';

export function OrganisationsFilterCombobox({
  value,
  onChange,
  disabled = false,
  className,
  placeholder = 'All Accounts',
}: OrganisationsFilterComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const infiniteQuery = useOrganisationsInfinite(debouncedSearch);

  const loadedOrganisations = useMemo(
    () => infiniteQuery.data?.pages.flatMap((page) => page.organisations) ?? [],
    [infiniteQuery.data],
  );

  const total = infiniteQuery.data?.pages[0]?.total ?? 0;

  // Selected label: prefer loaded pages, fall back to single-lookup
  const isExplicitValue = !!value && value !== ALL_VALUE;
  const selectedFromLoaded = isExplicitValue
    ? loadedOrganisations.find((organisation) => organisation._id === value)
    : undefined;
  const fallbackLookupId = isExplicitValue && !selectedFromLoaded ? value : undefined;
  const fallbackQuery = useOrganisation(fallbackLookupId);
  const selected = selectedFromLoaded ?? fallbackQuery.data ?? null;

  // Reset search when popover closes
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  // IntersectionObserver for infinite scroll
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          infiniteQuery.hasNextPage &&
          !infiniteQuery.isFetchingNextPage
        ) {
          void infiniteQuery.fetchNextPage();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    open,
    infiniteQuery.hasNextPage,
    infiniteQuery.isFetchingNextPage,
    infiniteQuery.fetchNextPage,
  ]);

  const triggerLabel = selected?.name ?? placeholder;
  const showingLabel =
    total > 0 ? `Showing ${loadedOrganisations.length} of ${total}` : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'h-7 w-full text-xs justify-between font-normal',
            className,
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search accounts..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {infiniteQuery.isError
                ? 'Failed to load. Click to retry.'
                : infiniteQuery.isLoading
                  ? 'Loading...'
                  : 'No accounts found.'}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => {
                  onChange(ALL_VALUE);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    !value || value === ALL_VALUE ? 'opacity-100' : 'opacity-0',
                  )}
                />
                {placeholder}
              </CommandItem>
              {loadedOrganisations.map((organisation) => (
                <CommandItem
                  key={organisation._id}
                  value={organisation._id}
                  onSelect={() => {
                    onChange(organisation._id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === organisation._id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{organisation.name}</span>
                </CommandItem>
              ))}
              {infiniteQuery.hasNextPage && (
                <div
                  ref={sentinelRef}
                  className="flex items-center justify-center py-2 text-xs text-muted-foreground"
                >
                  {infiniteQuery.isFetchingNextPage ? (
                    <>
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      Loading more...
                    </>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </div>
              )}
            </CommandGroup>
          </CommandList>
          {showingLabel && (
            <div className="border-t px-2 py-1 text-xs text-muted-foreground">
              {showingLabel}
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
